import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, ConfirmDialog, DataTable, DatePicker, FormField, Input, Modal, PageHeader,
  formatDateWithYear, type ColumnDef, type SortState,
} from '@shared-ui/components';
import {
  billingApi, codeStatus, formatRupees, todayIst, CODE_STATUS_LABEL, PLAN_LABEL, PURCHASABLE_PLANS,
  type BillingCode, type BillingCodeFields, type CodeStatus, type Order, type PurchasablePlan,
} from '../../api/billingApi';
import './Billing.css';

/**
 * Discount codes — one percentage, the plans it applies to and its valid dates
 * (Indian calendar, inclusive). A code is never deleted: switching it off stops
 * it, and its uses stay readable. Uses are counted from the orders, never
 * stored on the code.
 *
 * Design: docs/FeatureDesigns/billing/subscriptions-and-payments-target.md §6.3, §11.
 */

const STATUS_VARIANT: Record<CodeStatus, 'success' | 'info' | 'default' | 'warning'> = {
  active: 'success', upcoming: 'info', expired: 'default', off: 'warning',
};
const STATUS_RANK: Record<CodeStatus, number> = { active: 0, upcoming: 1, expired: 2, off: 3 };

const plansLabel = (plans: PurchasablePlan[]) => plans.map(p => PLAN_LABEL[p] ?? p).join(', ');

export default function Codes() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['billingCodes'], queryFn: billingApi.listCodes });
  const [sort, setSort] = useState<SortState>({ sortBy: 'status', sortOrder: 'asc' });
  const [editing, setEditing] = useState<BillingCode | 'new' | null>(null);
  const [switchingOff, setSwitchingOff] = useState<BillingCode | null>(null);
  const [usesOf, setUsesOf] = useState<BillingCode | null>(null);
  const today = todayIst();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['billingCodes'] });

  const save = useMutation({
    mutationFn: ({ id, fields, isNew }: { id: string; fields: BillingCodeFields; isNew: boolean }) =>
      (isNew ? billingApi.createCode({ id, ...fields }) : billingApi.updateCode(id, fields)),
    onSuccess: () => { void invalidate(); setEditing(null); },
  });

  const switchOff = useMutation({
    mutationFn: (code: BillingCode) => billingApi.updateCode(code.id, {
      discountPct: code.discountPct, plans: code.plans,
      validFrom: code.validFrom, validUntil: code.validUntil, active: false,
    }),
    onSuccess: () => { void invalidate(); setSwitchingOff(null); },
  });

  const columns: ColumnDef<BillingCode>[] = [
    {
      key: 'code', header: 'Code', sortable: true, mobile: 'title', sortValue: c => c.id,
      render: c => <span className="bill-mono">{c.id}</span>,
    },
    {
      key: 'discount', header: 'Discount', sortable: true, className: 'bill-num',
      sortValue: c => c.discountPct, render: c => `${c.discountPct}%`,
    },
    { key: 'plans', header: 'Plans', render: c => plansLabel(c.plans) },
    {
      key: 'valid', header: 'Valid (IST)', sortable: true, sortValue: c => c.validFrom,
      render: c => `${formatDateWithYear(c.validFrom)} – ${formatDateWithYear(c.validUntil)}`,
    },
    {
      key: 'status', header: 'Status', sortable: true, mobile: 'accessory',
      sortValue: c => STATUS_RANK[codeStatus(c, today)],
      render: c => {
        const s = codeStatus(c, today);
        return <Badge variant={STATUS_VARIANT[s]} size="sm">{CODE_STATUS_LABEL[s]}</Badge>;
      },
    },
    {
      key: 'uses', header: 'Uses', sortable: true, className: 'bill-num', sortValue: c => c.uses ?? 0,
      render: c => (
        <button
          type="button" className="bill-link-btn" disabled={!c.uses}
          onClick={e => { e.stopPropagation(); setUsesOf(c); }}
        >
          {(c.uses ?? 0).toLocaleString()}
        </button>
      ),
    },
    {
      key: 'actions', header: '', hideable: false,
      render: c => (
        <div className="bill-row-actions" onClick={e => e.stopPropagation()}>
          <Button variant="outline" size="sm" onClick={() => { save.reset(); setEditing(c); }}>Edit</Button>
          {c.active && codeStatus(c, today) !== 'expired' && (
            <Button variant="ghost" size="sm" onClick={() => { switchOff.reset(); setSwitchingOff(c); }}>Switch off</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bill">
      <PageHeader
        title="Codes"
        subtitle="Discount codes for Plus and Pro. Dates are the Indian calendar, both days included. Uses are fulfilled orders that carried the code."
        actions={<Button variant="primary" size="sm" onClick={() => { save.reset(); setEditing('new'); }}>+ New Code</Button>}
      />

      {error
        ? <p className="bill-error">Error: {(error as Error).message}</p>
        : (
          <DataTable<BillingCode>
            columns={columns}
            data={data ?? []}
            rowKey={c => c.id}
            sort={sort}
            onSortChange={setSort}
            loading={isLoading}
            mobileCards
            emptyMessage="No codes yet. Create one with + New Code."
          />
        )}

      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New code' : editing ? `Edit ${editing.id}` : undefined}
        size="md"
      >
        {editing !== null && (
          <CodeForm
            code={editing === 'new' ? null : editing}
            saving={save.isPending}
            error={save.error as Error | null}
            onCancel={() => setEditing(null)}
            onSave={(id, fields) => save.mutate({ id, fields, isNew: editing === 'new' })}
          />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={switchingOff !== null}
        message={
          <>
            Switch off <strong>{switchingOff?.id}</strong>? It stops applying straight away; its uses stay on record.
            {switchOff.isError && <p className="bill-error">{(switchOff.error as Error).message}</p>}
          </>
        }
        confirmLabel="Switch off"
        confirmVariant="danger"
        busy={switchOff.isPending}
        onConfirm={() => switchingOff && switchOff.mutate(switchingOff)}
        onCancel={() => setSwitchingOff(null)}
      />

      <Modal
        isOpen={usesOf !== null}
        onClose={() => setUsesOf(null)}
        title={usesOf ? `Uses of ${usesOf.id}` : undefined}
        size="lg"
        closeOnOverlayClick
        closeOnEscape
      >
        {usesOf && <CodeUses code={usesOf} />}
      </Modal>
    </div>
  );
}

function CodeForm({ code, saving, error, onCancel, onSave }: {
  code: BillingCode | null;
  saving: boolean;
  error: Error | null;
  onCancel: () => void;
  onSave: (id: string, fields: BillingCodeFields) => void;
}) {
  const isNew = !code;
  const [id, setId] = useState(code?.id ?? '');
  const [pct, setPct] = useState(code ? String(code.discountPct) : '');
  const [plans, setPlans] = useState<PurchasablePlan[]>(code?.plans ?? ['plus', 'pro']);
  const [validFrom, setValidFrom] = useState(code?.validFrom ?? todayIst());
  const [validUntil, setValidUntil] = useState(code?.validUntil ?? '');
  const [active, setActive] = useState(code?.active ?? true);

  const pctNum = Number(pct);
  const pctOk = Number.isInteger(pctNum) && pctNum >= 1 && pctNum <= 100;
  const datesOk = !!validFrom && !!validUntil && validFrom <= validUntil;
  const ready = (!isNew || id.trim().length >= 3) && pctOk && plans.length > 0 && datesOk;

  const togglePlan = (p: PurchasablePlan) =>
    setPlans(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));

  return (
    <form
      className="bill-form"
      onSubmit={e => {
        e.preventDefault();
        if (ready && !saving) {
          onSave(isNew ? id.trim().toUpperCase() : code.id, {
            discountPct: pctNum, plans, validFrom, validUntil, active,
          });
        }
      }}
    >
      {isNew && (
        <FormField label="Code" required className="bill-span">
          <Input value={id} onChange={e => setId(e.target.value.toUpperCase())} placeholder="DIWALI50" autoFocus />
          <p className="bill-sub">3–32 letters, digits or dashes. Fixed once created.</p>
        </FormField>
      )}

      <FormField label="Discount %" required error={pct && !pctOk ? 'A whole number from 1 to 100' : undefined}>
        <Input type="number" min={1} max={100} step={1} value={pct} onChange={e => setPct(e.target.value)} placeholder="20" />
      </FormField>

      <FormField label="Plans" required error={plans.length === 0 ? 'Choose at least one plan' : undefined}>
        <div className="bill-checks">
          {PURCHASABLE_PLANS.map(p => (
            <label key={p}>
              <input type="checkbox" checked={plans.includes(p)} onChange={() => togglePlan(p)} />
              {PLAN_LABEL[p]}
            </label>
          ))}
        </div>
      </FormField>

      <FormField label="Valid from (IST)" required>
        <DatePicker value={validFrom} onChange={setValidFrom} ariaLabel="Valid from" />
      </FormField>

      <FormField
        label="Valid until (IST)" required
        error={validFrom && validUntil && validFrom > validUntil ? 'The end date is before the start date' : undefined}
      >
        <DatePicker value={validUntil} onChange={setValidUntil} ariaLabel="Valid until" />
      </FormField>

      <div className="bill-span bill-checks">
        <label>
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
          Active — untick to switch the code off
        </label>
      </div>

      {error && <p className="bill-error bill-span">{error.message}</p>}

      <div className="bill-form-actions bill-span">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={!ready} loading={saving}>
          {isNew ? 'Create code' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function CodeUses({ code }: { code: BillingCode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['billingCodeUses', code.id],
    queryFn: () => billingApi.codeUses(code.id),
  });

  const columns: ColumnDef<Order>[] = [
    { key: 'date', header: 'Date', render: o => formatDateWithYear(o.fulfilledAt ?? o.createdAt) },
    {
      key: 'buyer', header: 'Buyer',
      render: o => <Link className="bill-mono" to={`/users/${encodeURIComponent(o.userId)}`}>{o.userId}</Link>,
    },
    { key: 'plan', header: 'Plan', render: o => PLAN_LABEL[o.plan] ?? o.plan },
    { key: 'list', header: 'List price', className: 'bill-num', render: o => formatRupees(o.listTotalPaise) },
    { key: 'paid', header: 'Price paid', className: 'bill-num', render: o => formatRupees(o.totalPaise) },
  ];

  if (error) return <p className="bill-error">Error: {(error as Error).message}</p>;
  return (
    <DataTable<Order>
      columns={columns}
      data={data ?? []}
      rowKey={o => o.id}
      loading={isLoading}
      loadingVariant="spinner"
      compact
      emptyMessage="No fulfilled orders have used this code."
    />
  );
}
